import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import financialReportService, { 
  FinancialSummary, 
  DetailedReport, 
  ServiceTypeStats, 
  FirmStats 
} from '../../services/financialReport.service';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const FinancialReports: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [detailedReport, setDetailedReport] = useState<DetailedReport | null>(null);
  const [serviceTypeStats, setServiceTypeStats] = useState<ServiceTypeStats[]>([]);
  const [firmStats, setFirmStats] = useState<FirmStats[]>([]);
  const [filters, setFilters] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    firm_id: '',
    service_type_id: '',
    status: '',
    payment_status: '',
  });

  const isAdmin = user?.roles?.some((role: any) => role.name === 'admin');

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Convert string filters to proper types
      const detailedFilters = {
        ...filters,
        firm_id: filters.firm_id ? parseInt(filters.firm_id) : undefined,
        service_type_id: filters.service_type_id ? parseInt(filters.service_type_id) : undefined,
      };
      
      const [summaryData, detailedData, serviceStats, firmStatsData] = await Promise.all([
        financialReportService.getFinancialSummary({
          start_date: filters.start_date,
          end_date: filters.end_date,
        }),
        financialReportService.getDetailedReport(detailedFilters),
        financialReportService.getServiceTypeStats({
          start_date: filters.start_date,
          end_date: filters.end_date,
        }),
        isAdmin ? financialReportService.getFirmStats({
          start_date: filters.start_date,
          end_date: filters.end_date,
        }) : Promise.resolve([]),
      ]);

      setSummary(summaryData);
      setDetailedReport(detailedData);
      setServiceTypeStats(serviceStats);
      setFirmStats(firmStatsData);
    } catch (error) {
      console.error('Error loading financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Convert string filters to proper types for export
      const exportFilters = {
        ...filters,
        firm_id: filters.firm_id ? parseInt(filters.firm_id) : undefined,
        service_type_id: filters.service_type_id ? parseInt(filters.service_type_id) : undefined,
      };
      
      const blob = await financialReportService.exportReport(exportFilters);
      const filename = `financial_report_${new Date().toISOString().split('T')[0]}.csv`;
      financialReportService.downloadCSV(blob, filename);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    const numAmount = Number(amount || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partial':
        return 'warning';
      case 'Unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Financial Reports
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Filters" />
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Firm</InputLabel>
                <Select
                  value={filters.firm_id}
                  label="Firm"
                  onChange={(e) => setFilters({ ...filters, firm_id: e.target.value })}
                >
                  <MenuItem value="">All Firms</MenuItem>
                  {/* Add firm options here */}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={filters.service_type_id}
                label="Service Type"
                onChange={(e) => setFilters({ ...filters, service_type_id: e.target.value })}
              >
                <MenuItem value="">All Services</MenuItem>
                {/* Add service type options here */}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Created">Created</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Evaluation_Completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={filters.payment_status}
                label="Payment Status"
                onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
              >
                <MenuItem value="">All Payments</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoneyIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.total_revenue)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Retainer Received
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.total_retainer_received)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Outstanding Balance
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.total_balance_outstanding)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Average Case Value
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(summary.average_case_value)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Detailed Report" />
            <Tab label="Service Type Stats" />
            {isAdmin && <Tab label="Firm Stats" />}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {detailedReport && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Case Details ({detailedReport.cases.length} cases)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Case Name</TableCell>
                      <TableCell>Firm</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment Status</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Retainer</TableCell>
                      <TableCell>Balance</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailedReport.cases.map((caseItem) => (
                      <TableRow key={caseItem.id}>
                        <TableCell>{caseItem.case_name}</TableCell>
                        <TableCell>{caseItem.firm_name}</TableCell>
                        <TableCell>{caseItem.service_type}</TableCell>
                        <TableCell>
                          <Chip label={caseItem.status.replace('_', ' ')} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={caseItem.payment_status} 
                            color={getPaymentStatusColor(caseItem.payment_status) as any}
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{formatCurrency(caseItem.total_amount)}</TableCell>
                        <TableCell>{formatCurrency(caseItem.retainer_amount)}</TableCell>
                        <TableCell>{formatCurrency(caseItem.balance_amount)}</TableCell>
                        <TableCell>{formatDate(caseItem.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Service Type Statistics
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service Type</TableCell>
                  <TableCell>Total Cases</TableCell>
                  <TableCell>Total Revenue</TableCell>
                  <TableCell>Service Fees</TableCell>
                  <TableCell>Rush Fees</TableCell>
                  <TableCell>Rush Cases</TableCell>
                  <TableCell>Average Value</TableCell>
                  <TableCell>Paid Cases</TableCell>
                  <TableCell>Unpaid Cases</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceTypeStats.map((stat) => (
                  <TableRow key={stat.service_type_id}>
                    <TableCell>{stat.service_type_name}</TableCell>
                    <TableCell>{stat.total_cases}</TableCell>
                    <TableCell>{formatCurrency(stat.total_revenue)}</TableCell>
                    <TableCell>{formatCurrency(stat.total_service_fees)}</TableCell>
                    <TableCell>{formatCurrency(stat.total_rush_fees)}</TableCell>
                    <TableCell>{stat.rush_cases}</TableCell>
                    <TableCell>{formatCurrency(stat.average_case_value)}</TableCell>
                    <TableCell>{stat.paid_cases}</TableCell>
                    <TableCell>{stat.unpaid_cases}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {isAdmin && (
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Firm Statistics
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Firm Name</TableCell>
                    <TableCell>Total Cases</TableCell>
                    <TableCell>Total Revenue</TableCell>
                    <TableCell>Retainer Received</TableCell>
                    <TableCell>Outstanding Balance</TableCell>
                    <TableCell>Rush Cases</TableCell>
                    <TableCell>Average Value</TableCell>
                    <TableCell>Paid Cases</TableCell>
                    <TableCell>Unpaid Cases</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {firmStats.map((stat) => (
                    <TableRow key={stat.firm_id}>
                      <TableCell>{stat.firm_name}</TableCell>
                      <TableCell>{stat.total_cases}</TableCell>
                      <TableCell>{formatCurrency(stat.total_revenue)}</TableCell>
                      <TableCell>{formatCurrency(stat.total_retainer_received)}</TableCell>
                      <TableCell>{formatCurrency(stat.total_balance_outstanding)}</TableCell>
                      <TableCell>{stat.rush_cases}</TableCell>
                      <TableCell>{formatCurrency(stat.average_case_value)}</TableCell>
                      <TableCell>{stat.paid_cases}</TableCell>
                      <TableCell>{stat.unpaid_cases}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        )}
      </Card>
    </Box>
  );
};

export default FinancialReports; 